import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLButtonElement} [closeDropdownButton] - The close dropdown button (inline search only).
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #userHasInteracted = false;

  #isClosing = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeSearchFinishTimeoutId = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    const setUserInteracted = () => {
      this.#userHasInteracted = true;
    };
    document.addEventListener('mousedown', setUserInteracted, { signal, once: true });
    document.addEventListener('keydown', setUserInteracted, { signal, once: true });
    document.addEventListener('touchstart', setUserInteracted, { signal, once: true });

    if (this.hasAttribute('data-hide-dropdown-when-empty')) {
      document.addEventListener(ThemeEvents.megaMenuHover, this.#handleMegaMenuHover, { signal });
      this.refs.searchInput.addEventListener(
        'focus',
        () => {
          if (!this.#userHasInteracted) {
            this.refs.searchInput.blur();
          }
        },
        { signal }
      );
      this.refs.searchInput.addEventListener(
        'blur',
        () => {
          if (!this.#isClosing) this.removeAttribute('data-closed');
        },
        { signal }
      );
    }

    this.refs.predictiveSearchResults.addEventListener('mousedown', this.#handleDragScrollStart, { signal });
  }

  #handleMegaMenuHover = () => {
    this.closeSearch({ refocus: false });
  };

  #handleDragScrollStart = (event) => {
    const wrapper = event.target.closest('.predictive-search-results__wrapper-queries');
    if (!wrapper) return;
    if (wrapper.scrollWidth <= wrapper.clientWidth) return;

    const startX = event.clientX;
    const startScrollLeft = wrapper.scrollLeft;
    let hasDragged = false;

    const handleMove = (moveEvent) => {
      hasDragged = true;
      wrapper.scrollLeft = startScrollLeft - (moveEvent.clientX - startX);
    };

    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      wrapper.style.cursor = '';
      wrapper.style.userSelect = '';
      if (hasDragged) {
        const preventClick = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          wrapper.removeEventListener('click', preventClick, true);
        };
        wrapper.addEventListener('click', preventClick, true);
        setTimeout(() => wrapper.removeEventListener('click', preventClick, true), 100);
      }
    };

    wrapper.style.cursor = 'grabbing';
    wrapper.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#closeSearchFinishTimeoutId != null) {
      clearTimeout(this.#closeSearchFinishTimeoutId);
      this.#closeSearchFinishTimeoutId = null;
    }
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  handleInlineFocus = () => {
    if (!this.#userHasInteracted) return;
    if (this.hasAttribute('data-closed')) return;
    this.refs.searchInput.setAttribute('aria-expanded', 'true');
  };

  handleInlineClick = () => {
    if (!this.hasAttribute('data-hide-dropdown-when-empty')) return;
    if (!this.hasAttribute('data-closed')) return;
    this.removeAttribute('data-closed');
    this.refs.searchInput.setAttribute('aria-expanded', 'true');
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (this.hasAttribute('data-hide-dropdown-when-empty')) {
        this.closeSearch();
      } else {
        this.refs.searchInput.focus();
        this.#resetSearch();
      }
      return;
    }

    const isDropdownOpen =
      !this.hasAttribute('data-hide-dropdown-when-empty') || this.refs.searchInput.value.length > 0;
    if (event.key === 'Tab' && !isDropdownOpen) {
      return;
    }

    const closeButton = this.refs.closeDropdownButton;
    const hasCloseButton = closeButton instanceof HTMLElement;
    const activeElement = document.activeElement;

    if (event.key === 'Tab' && hasCloseButton) {
      const currentIndex = this.#currentIndex;
      const totalItems = this.#allResultsItems?.length ?? 0;

      if (
        activeElement === this.refs.searchInput &&
        !event.shiftKey &&
        currentIndex < 0 &&
        this.refs.searchInput.value.length > 0
      ) {
        event.preventDefault();
        closeButton.focus();
        return;
      }

      if (activeElement === closeButton) {
        event.preventDefault();
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          this.#currentIndex = totalItems - 1;
          this.refs.searchInput.focus();
        } else if (totalItems > 0) {
          this.#isKeyboardNavigation = true;
          this.#currentIndex = 0;
        } else {
          this.refs.searchInput.focus();
        }
        return;
      }

      if (totalItems > 0) {
        if (currentIndex === 0 && event.shiftKey) {
          event.preventDefault();
          closeButton.focus();
          return;
        }
        if (currentIndex === totalItems - 1 && !event.shiftKey) {
          event.preventDefault();
          closeButton.focus();
          return;
        }
      }
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    } else {
      const active = document.activeElement;
      if (active instanceof HTMLElement && this.contains(active)) {
        active.blur();
      }
      this.refs.searchInput.blur();
    }
    this.#resetSearch();
  }, 100);

  closeSearch = (options) => {
    const refocusAfterClose = !(
      options && typeof options === 'object' && 'refocus' in options && options.refocus === false
    );

    if (this.hasAttribute('data-hide-dropdown-when-empty')) {
      if (this.#closeSearchFinishTimeoutId != null) {
        clearTimeout(this.#closeSearchFinishTimeoutId);
        this.#closeSearchFinishTimeoutId = null;
      }

      this.#isClosing = true;
      this.setAttribute('data-closed', 'true');
      const active = document.activeElement;
      if (active instanceof HTMLElement && this.contains(active)) {
        active.blur();
      }
      this.resetSearch(false);
      this.#closeSearchFinishTimeoutId = setTimeout(() => {
        this.#closeSearchFinishTimeoutId = null;
        if (refocusAfterClose) {
          this.refs.searchInput.focus();
        }
        this.#isClosing = false;
      }, 150);
    } else {
      this.resetSearch(false);
    }
  };

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    if (this.hasAttribute('data-hide-dropdown-when-empty')) {
      this.refs.searchInput.setAttribute('aria-expanded', 'true');
    }

    if (!event.inputType) return;

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,tag,body');
    this.#appendCommonSearchParams(url);

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #appendCommonSearchParams(url) {
    const resultsId = this.dataset.resultsId;
    if (resultsId) {
      url.searchParams.set('results_id', resultsId);
    }
    const imageRatio = document.getElementById('header-component')?.getAttribute('data-header-product-card-image-ratio');
    if (imageRatio) {
      url.searchParams.set('image_ratio', imageRatio);
    }
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    if (this.hasAttribute('data-hide-dropdown-when-empty')) {
      this.refs.searchInput.setAttribute('aria-expanded', 'false');
    }

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    this.#appendCommonSearchParams(url);

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
